import { useNotification, useTranslation } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl';

import { ServiceAgreementDeclineReasonEnum } from '../api/generated/pm';
import {
  useAcceptServiceAgreement,
  useDeclineServiceAgreement,
} from '../pages/Customers/customers.mutations';
import { useErrorDrawer } from '../providers/ErrorDrawerContext/error-drawer.context';
import { getErrorStatus } from '../utils/errorStatus';

/**
 * The hey-api client throws the parsed error body of an HTTP error response
 * (the shared interceptor attaches `.status`); the backend's body carries a
 * human-readable `message`. Network failures and empty bodies yield nothing so
 * the caller keeps its translated fallback.
 */
function getErrorBodyMessage(error: unknown): string | undefined {
  if (getErrorStatus(error) === undefined) return undefined;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' ? message : undefined;
}

function useServiceAgreementId(id: string) {
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { t } = useTranslation();
  const {
    mutateAsync: acceptServiceAgreementMutation,
    isPending: isPendingAcceptServiceAgreement,
  } = useAcceptServiceAgreement();

  const {
    mutateAsync: declineServiceAgreementMutation,
    isPending: isPendingDeclineServiceAgreement,
  } = useDeclineServiceAgreement();

  const declineAgreement = async (reason: ServiceAgreementDeclineReasonEnum) => {
    try {
      await declineServiceAgreementMutation({ id, payload: { declineReason: reason } });
    } catch (error: unknown) {
      if (!showError(error)) {
        const heading = t('serviceAgreement.resultMessages.declineErrorTitle');
        const body =
          getErrorBodyMessage(error) ??
          t('serviceAgreement.resultMessages.declineErrorDescription');
        notificationService.addNotification(heading, body, Statuses.error);
      }
      return Promise.reject(error);
    }
  };

  const acceptAgreement = async () => {
    try {
      await acceptServiceAgreementMutation({ id });
    } catch (error: unknown) {
      if (!showError(error)) {
        const heading = t('serviceAgreement.resultMessages.acceptErrorTitle');
        const body =
          getErrorBodyMessage(error) ?? t('serviceAgreement.resultMessages.acceptErrorDescription');
        notificationService.addNotification(heading, body, Statuses.error);
      }
      return Promise.reject(error);
    }
  };

  return {
    acceptAgreement,
    isPendingAcceptServiceAgreement,
    declineAgreement,
    isPendingDeclineServiceAgreement,
  };
}

export default useServiceAgreementId;
